import { type NavigationGuard } from 'vue-router';
import { startSmCookieRefresh } from '@/composables/useSmCookieRefresh';
import { getStoredLocale, loadLocaleMessages } from '@/plugins/i18n';
import { pendoSetup } from '@/plugins/pendo';
import { rolloutFlagsSetup } from '@/plugins/rollout-flags';
import { setSentryUser } from '@/plugins/sentry';
import { useAppConfigStore } from '@/stores/appConfig';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { useCurrencyStore } from '@/stores/currency';
import { useMergeStore } from '@/stores/merge';
import {
  ACCESS_TOKEN,
  IS_PUBLIC,
  NO_AUTH_NEEDED,
  PBE_EXTENSION_IDS,
  REFRESH_TOKEN,
  STANDALONE_PBE_LOGIN_PARAM,
} from '@/types';

export const loadAppConfig: NavigationGuard = async (to, from, next) => {
  const appConfigStore = useAppConfigStore();
  await appConfigStore.fetchAppConfig();
  next();
};

/**
 * This guard runs before every route change.
 * If the user is logged in or the route is marked as noAuthNeeded the navigation continues.
 * If the user is not logged in, it tries to set the user profile and the loggedIn state.
 * If that fails, it redirects the user to the 'welcome-back' page with the original route as a query parameter.
 */
export const handleNonLoggedInUsers: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();
  const noAuthNeeded = to.meta[NO_AUTH_NEEDED];

  if (noAuthNeeded || authStore.loggedIn) {
    if (noAuthNeeded) {
      await loadLocaleMessages(getStoredLocale());
    }
    next();
  } else {
    authStore.path = to.fullPath;

    const hasToken = !!localStorage.getItem(ACCESS_TOKEN);

    if (!hasToken) {
      return next({ name: 'welcome-back', query: { redirect: to.fullPath } });
    }

    // If successful it sets the user profile and the loggedIn state in the store
    // If not successful it redirects the user to the 'welcome-back' page via interceptor
    await authStore.setUserProfileAndLoggedInState();

    if (authStore.loggedIn) {
      next();
    } else {
      // redundant since the interceptor will handle it but necessary to avoid warning from vue-router
      next({ name: 'welcome-back', query: { redirect: to.fullPath } });
    }
  }
};

export const handleFirstLogin: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  if (
    authStore.loggedIn &&
    !companyStore.isAdpTenant &&
    !authStore.profile.has_accepted_terms &&
    to.name !== 'first-login'
  ) {
    next({ name: 'first-login' });
  } else {
    next();
  }
};

export const handleTempPasswordChange: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();

  if (
    authStore.loggedIn &&
    !companyStore.isUsingSSO &&
    !companyStore.isAdpTenant &&
    authStore.profile.is_temp_password &&
    to.name !== 'temp-password-change' &&
    to.name !== 'first-login'
  ) {
    next({ name: 'temp-password-change' });
  } else {
    next();
  }
};

/**
 * Sets the company store data for non public routes
 */
export const handleNonPublicRoutes: NavigationGuard = async (to, from, next) => {
  const companyStore = useCompanyStore();

  /**
   * in prod authentication is run via SSO where from.name is not 'welcome-back'
   * we had to check if the company object empty or not and if the company id is not present
   */
  const hasToken = !!localStorage.getItem(ACCESS_TOKEN);

  if (
    !to.meta[IS_PUBLIC] &&
    (from.name === 'welcome-back' || !Object.keys(companyStore.company).length || !companyStore.company?.id) &&
    hasToken
  ) {
    await companyStore.fetchCompanies();
    // We don't know the locale of the user yet, getStoredLocale will try to load it from localStorage or use the company locale
    await loadLocaleMessages(getStoredLocale());
  }

  return next();
};

/**
 * Bootstrap things for logged in users (setup heap, appcues etc)
 * Called only once
 */
let loggedInBootstrapped = false;

export const loggedInActions: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();
  const companyStore = useCompanyStore();
  const currencyStore = useCurrencyStore();
  const mergeStore = useMergeStore();

  if (authStore.loggedIn && !loggedInBootstrapped) {
    await Promise.all([
      loadLocaleMessages(getStoredLocale()),
      currencyStore.fetchFxRates(),
      companyStore.isUsingMerge ? mergeStore.createLinkToken() : Promise.resolve(),
      rolloutFlagsSetup(authStore.profile),
      setSentryUser(authStore.profile.email),
      pendoSetup(authStore.profile, companyStore.company),
    ]);

    startSmCookieRefresh();
    loggedInBootstrapped = true;
  }

  return next();
};

/**
 * Removes the splash loader from DOM
 * Use last in the chain of guards
 */
export const removeSplashLoader: NavigationGuard = async (to, from, next) => {
  const splashLoader = document.getElementById('p-splash-loader');

  if (splashLoader) {
    splashLoader.classList.add('p-splash-fade-out');
    setTimeout(() => {
      splashLoader.remove();
    }, 400);
  }

  return next();
};

export const savePBELoginFlag: NavigationGuard = async (to, from, next) => {
  if (to.query[STANDALONE_PBE_LOGIN_PARAM]) {
    localStorage.setItem(STANDALONE_PBE_LOGIN_PARAM, 'true');
  }
  next();
};

export const handlePBELogin: NavigationGuard = async (to, from, next) => {
  const authStore = useAuthStore();
  if (authStore.loggedIn && localStorage.getItem(STANDALONE_PBE_LOGIN_PARAM)) {
    for (const extensionId of PBE_EXTENSION_IDS) {
      try {
        await chrome?.runtime?.sendMessage(extensionId, {
          accessToken: localStorage.getItem(ACCESS_TOKEN),
          refreshToken: localStorage.getItem(REFRESH_TOKEN),
        });
      } catch (e) {
        console.log(`Error sending message to extension with id ${extensionId}`);
      }
    }

    localStorage.removeItem(STANDALONE_PBE_LOGIN_PARAM);
    window.close();
  }
  next();
};
